import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { Button, Form, IconSpinner, NamedLink } from '../../components';
import css from './ExtraPackagesForm.css';
import EditListingPricingPagePackagesList from '../../components/EditListingPricingPagePackagesList/EditListingPricingPagePackagesList';
import { useDispatch, useSelector } from 'react-redux';
import { selectOwnRiderListings } from '../../ducks/riderListings.duck';
import {
    PROFILE_PACKAGE_SOCIAL_BOOST_ID,
    PROFILE_PACKAGE_ANONYM_PROFILE_ID,
    PROFILE_PACKAGE_PRIVATE_PROFILE_ID,
    extraPackagesProfileConfig,
} from '../../marketplace-custom-config';
import { updateUserProfileInfo } from '../../ducks/user.duck';
import { updateOwnListing } from '../ManageListingsPage/ManageListingsPage.duck';
import { useBottomBarToggler } from './useBottomBarToggler';
import { getSmallestPackagePrice } from '../../util/extraPackages';
import config from '../../config';
import { useRedirectToProfilePage } from './useRedirectToProfilePage';
import { isProfileComplete } from '../../util/user';
import AlertProfileNotComplete from '../../components/EditListingPricingPagePackagesList/AlertProfileNotComplete';
import classNames from 'classnames';

const { userTypeRider } = config;

const smallestPackagePrice = getSmallestPackagePrice(extraPackagesProfileConfig);

const Sidenote = ({ rootClassName }) => (
    <p className={classNames(css.pricingPageSideNote, rootClassName)}>
        Besuche die Seite{' '}
        <NamedLink
            openInNewTab
            name="PricingPage"
            to={{
                hash: '#wie-viele-credits-brauche-ich',
            }}
        >
            Preisgestaltung
        </NamedLink>
        , um mehr über Funktionen und Preise zu erfahren.
    </p>
);

const ExtraPackagesForm = ({ redirectToProfilePage, ...rest }) => {
    const [ready, setReady] = useState(false);

    useBottomBarToggler();

    useRedirectToProfilePage({ ready, redirectToProfilePage });

    const dispatch = useDispatch();
    const updateUserProfile = params => dispatch(updateUserProfileInfo(params));
    const updateRiderListing = (listingId, params) => dispatch(updateOwnListing(listingId, params));

    const currentUser = useSelector(s => s.user.currentUser);
    const currentUserId = currentUser?.id?.uuid;

    /**
     * fetched once per session by middlewares/currentUser.js, read here rather
     * than re-fetched (see riderListings.duck.js:selectOwnRiderListings)
     */
    const [riderListings, loading] = useSelector(state =>
        selectOwnRiderListings(state, currentUserId)
    );

    const riderListing = Array.isArray(riderListings)
        ? riderListings.find(
              ({
                  attributes: {
                      publicData: { userRepresentationId },
                  },
              }) => !!userRepresentationId
          )
        : null;

    if (loading) return <IconSpinner />;

    const { isPrimaryFieldsComplete } = isProfileComplete(currentUser);
    const riderListingId = riderListing?.id?.uuid;
    const { extrapackagesData = {} } = riderListing?.attributes?.publicData || {};

    const handleSubmit = values => {
        const extrapackagesData = {
            [PROFILE_PACKAGE_SOCIAL_BOOST_ID]: values[PROFILE_PACKAGE_SOCIAL_BOOST_ID] || null,
            [PROFILE_PACKAGE_ANONYM_PROFILE_ID]: values[PROFILE_PACKAGE_ANONYM_PROFILE_ID] || null,
            [PROFILE_PACKAGE_PRIVATE_PROFILE_ID]:
                values[PROFILE_PACKAGE_PRIVATE_PROFILE_ID] || null,
        };
        const params = {
            publicData: { extrapackagesData },
        };
        const userParams = {
            publicData: { extrapackagesData },
        };

        updateUserProfile(userParams);
        updateRiderListing(riderListingId, params);

        setReady(true);
    };

    const initialValues = {
        [PROFILE_PACKAGE_SOCIAL_BOOST_ID]: extrapackagesData[PROFILE_PACKAGE_SOCIAL_BOOST_ID],
        [PROFILE_PACKAGE_ANONYM_PROFILE_ID]: extrapackagesData[PROFILE_PACKAGE_ANONYM_PROFILE_ID],
        [PROFILE_PACKAGE_PRIVATE_PROFILE_ID]: extrapackagesData[PROFILE_PACKAGE_PRIVATE_PROFILE_ID],
    };

    return (
        <FinalForm
            {...rest}
            onSubmit={handleSubmit}
            /**
             * keepDirtyOnReinitialize prop is needed to preserve
             * form's values on touch start\move\end for mobile devices
             */
            keepDirtyOnReinitialize
            initialValues={initialValues}
            render={fieldRenderProps => {
                const { handleSubmit, values, initialValues } = fieldRenderProps;

                const packagesList = [
                    PROFILE_PACKAGE_SOCIAL_BOOST_ID,
                    PROFILE_PACKAGE_ANONYM_PROFILE_ID,
                    PROFILE_PACKAGE_PRIVATE_PROFILE_ID,
                ];

                const untouched = packagesList.every(
                    id => Boolean(values[id]) === Boolean(initialValues[id])
                );
                const anyOfPackageSelected = packagesList.some(id => Boolean(values[id]));

                const creditsAvailable =
                    currentUser?.attributes?.profile?.privateData?.creditsAvailable;
                const creditsAvailableByType = (creditsAvailable || {})[userTypeRider];
                const lessThanSmallestPackagePrice =
                    anyOfPackageSelected && creditsAvailableByType < smallestPackagePrice;

                const noPackagesSelected =
                    Object.values(initialValues).filter(s => !!s).length === 0;

                return (
                    <Form onSubmit={handleSubmit} className={css.form}>
                        <h3>Zusatzpakete</h3>
                        {!isPrimaryFieldsComplete && <AlertProfileNotComplete />}
                        <EditListingPricingPagePackagesList
                            packagesType="profile"
                            values={values}
                            currentUser={currentUser}
                            currentListing={riderListing}
                            rootClassName={css.root}
                            disabled={!isPrimaryFieldsComplete}
                            renderSubmitButton={({ disabled }) => (
                                <>
                                    <Sidenote rootClassName={css.mob} />

                                    <footer className={css.proceedWithFooter}>
                                        <Button
                                            type="submit"
                                            className={css.actionButton}
                                            disabled={
                                                disabled ||
                                                untouched ||
                                                lessThanSmallestPackagePrice ||
                                                !isPrimaryFieldsComplete
                                            }
                                            ready={ready}
                                        >
                                            {noPackagesSelected
                                                ? 'Aktivieren'
                                                : 'Änderungen speichern'}
                                        </Button>

                                        <Sidenote rootClassName={css.desktop} />
                                    </footer>
                                </>
                            )}
                        />
                    </Form>
                );
            }}
        />
    );
};

export default ExtraPackagesForm;
